/**
 * V6-011 砺锋台 — 棋盘生成器
 *
 * 哈希种子分配关卡位置（同 levelId → 同位置），每页 65 关 + 地形填满
 * 14 列 × 11 行网格，棋子 1×2（占 2 列），7 子/行 × 11 行 = 77 槽位
 * 关卡:地形 = 85:15（65 关 + 12 地形 = 77 填满）
 *
 * @date 2026-07-21
 */

import { type BoardPage, type LevelEntry, LEVELS_PER_PAGE } from './types';
import type { BoardPiece } from './tactical-map';

// ── 地形数据集（从 screen-10-sharpen.ts 提取，V6-011 统一管理）──

export interface TerrainDef {
  svgPath: string;
  terrainName: string;
  terrainSize?: "1x2" | "2x2";
}

export const TERRAIN_DEFS: TerrainDef[] = [
  { svgPath: "cover/tr-c-01-孤石.svg",             terrainName: "孤石" },
  { svgPath: "cover/tr-c-02-岩石群.svg",           terrainName: "岩石群" },
  { svgPath: "cover/tr-c-04-倒木.svg",             terrainName: "倒木" },
  { svgPath: "obstacle/tr-o-01-垂直岩壁.svg",      terrainName: "垂直岩壁" },
  { svgPath: "obstacle/tr-o-03-深谷裂缝.svg",      terrainName: "深谷裂缝" },
  { svgPath: "obstacle/tr-o-07-河流横段.svg",      terrainName: "河流横段" },
  { svgPath: "obstacle/tr-o-09-沼泽.svg",          terrainName: "沼泽" },
  { svgPath: "obstacle/tr-o-06-山体主峰.svg",      terrainName: "山体主峰",   terrainSize: "2x2" },
  { svgPath: "unreachable/tr-u-03-绝壁边缘.svg",   terrainName: "绝壁边缘",   terrainSize: "2x2" },
  { svgPath: "unreachable/tr-u-04-深水区.svg",     terrainName: "深水区" },
  { svgPath: "unreachable/tr-u-05-禁区.svg",       terrainName: "禁区" },
];

/** 1×2 地形池（用于自动填充，排除 2×2 避免放置冲突） */
const TERRAIN_1X2 = TERRAIN_DEFS.filter((t) => t.terrainSize !== "2x2");

// ── 网格常量 ──

const GRID_COLS = 14;
const GRID_ROWS = 11;
const COLS_PER_PIECE = 2;
const SLOTS_PER_ROW = GRID_COLS / COLS_PER_PIECE; // 7
const TOTAL_SLOTS = SLOTS_PER_ROW * GRID_ROWS;    // 77

/** 预计算所有合法槽位（col 从 0 开始，步进 2） */
const ALL_SLOTS: { col: number; row: number }[] = [];
for (let row = 0; row < GRID_ROWS; row++) {
  for (let col = 0; col < GRID_COLS; col += COLS_PER_PIECE) {
    ALL_SLOTS.push({ col, row });
  }
}

// ── 哈希种子（djb2 变体，保证同 levelId 每次访问同一格）──

function hashSeed(id: string): number {
  let h = 5381;
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) + h + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * 为所有关卡分配稳定槽位
 * 哈希 → 线性探测 → 确保不冲突
 */
function assignAllSlots(levels: LevelEntry[]): Map<string, number> {
  const used = new Set<number>();
  const result = new Map<string, number>();

  for (const level of levels) {
    let h = hashSeed(level.id) % TOTAL_SLOTS;
    let attempts = 0;
    while (used.has(h) && attempts < TOTAL_SLOTS) {
      h = (h + 1) % TOTAL_SLOTS;
      attempts++;
    }
    if (attempts >= TOTAL_SLOTS) {
      console.warn(`[board-gen] 无法为 ${level.id} 分配位置（槽位已满）`);
      continue;
    }
    used.add(h);
    result.set(level.id, h);
  }

  return result;
}

// ── difficulty → rank 显示 ──

function getRankDisplay(difficulty: string): { name: string; idx: number } {
  const map: Record<string, [string, number]> = {
    tutorial: ["★ 战士", 0],
    easy: ["★ 战士", 0],
    normal: ["★★ 军官", 1],
    hard: ["★★★ 校官", 2],
    expert: ["★★★★ 将帅", 3],
  };
  return { name: map[difficulty]?.[0] ?? "★ 战士", idx: map[difficulty]?.[1] ?? 0 };
}

// ── 公开 API ──

/** 计算关卡总页数 */
export function computeTotalPages(levels: LevelEntry[]): number {
  return Math.max(1, Math.ceil(levels.length / LEVELS_PER_PAGE));
}

/**
 * 生成一页棋盘
 *
 * @param levels       该类型全部关卡（用于全局哈希分配保证位置稳定）
 * @param pageIdx      目标页码（0-based，自动 clamp 到有效范围）
 * @param clearedLevels 已通关关卡 ID 集合
 * @returns BoardPage（含 pieces 和 totalPages）
 */
export function generateBoardPage(
  levels: LevelEntry[],
  pageIdx: number,
  clearedLevels: Set<string>,
  failedLevels: Set<string>,
): BoardPage {
  const totalPages = computeTotalPages(levels);
  const page = Math.min(Math.max(0, pageIdx), totalPages - 1);

  const startIdx = page * LEVELS_PER_PAGE;
  const pageLevels = levels.slice(startIdx, startIdx + LEVELS_PER_PAGE);

  // 没有可用关卡时保持空棋盘，由屏幕层显示明确空态/错误态。
  if (levels.length === 0) {
    return { pageIdx: 0, totalPages: 1, pieces: [] };
  }

  // 全局分配位置（保证跨页稳定）
  const slotMap = assignAllSlots(levels);

  // 记录本页已占槽位
  const usedSlots = new Set<number>();
  const pieces: BoardPiece[] = [];

  // 找出第一个未通关的关卡（设为 current）
  let foundCurrent = false;
  const firstUnclearedId = levels.find((l) => !clearedLevels.has(l.id))?.id;

  // 构建关卡棋子
  for (const level of pageLevels) {
    const slotIdx = slotMap.get(level.id);
    if (slotIdx === undefined) continue;
    usedSlots.add(slotIdx);
    const slot = ALL_SLOTS[slotIdx];
    const rank = getRankDisplay(level.difficulty);

    let status: BoardPiece["status"];
    if (clearedLevels.has(level.id)) {
      status = "win";
    } else if (failedLevels.has(level.id)) {
      status = "loss";                       // 已挑战失败
    } else if (level.id === firstUnclearedId && !foundCurrent) {
      status = "current";                    // 当前可挑战
      foundCurrent = true;
    } else {
      status = "locked";                     // 从未挑战
    }

    pieces.push({
      id: level.id,
      type: "chess",
      col: slot.col,
      row: slot.row,
      name: level.name,
      rank: rank.name,
      rankIdx: rank.idx,
      status,
      subtitle: `${level.name} · ${level.type}`,
    });
  }

  // 填充剩余槽位为地形（填满整页，关卡:地形 = 85:15）
  const availableSlots: number[] = [];
  for (let i = 0; i < TOTAL_SLOTS; i++) {
    if (!usedSlots.has(i)) availableSlots.push(i);
  }

  let t = 0;
  for (const slotIdx of availableSlots) {
    const terrain = TERRAIN_1X2[t % TERRAIN_1X2.length];
    t++;
    const slot = ALL_SLOTS[slotIdx];
    pieces.push({
      id: `tr-p${page}-s${slotIdx}`,
      type: "terrain",
      col: slot.col,
      row: slot.row,
      name: terrain.terrainName,
      rank: "",
      rankIdx: 0,
      status: "locked",
      terrainSvg: terrain.svgPath,
      terrainName: terrain.terrainName,
      terrainSize: terrain.terrainSize,
    });
  }

  return { pageIdx: page, totalPages, pieces };
}
